<script lang="ts">
  import type { Snippet } from 'svelte';

  interface Props {
    open: boolean;
    title?: string;
    onclose: () => void;
    children: Snippet;
    /** プレビュー演出再生中などにシートを半透明化して背後を見えるようにする */
    fadeOut?: boolean;
  }

  let { open, title, onclose, children, fadeOut = false }: Props = $props();

  function handleBackdrop(e: MouseEvent): void {
    if (e.target === e.currentTarget) onclose();
  }

  function handleKeydown(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
      e.preventDefault();
      onclose();
    }
  }
</script>

<svelte:window onkeydown={open ? handleKeydown : undefined} />

{#if open}
  <div
    class="sheet-backdrop"
    class:fade-out={fadeOut}
    onclick={handleBackdrop}
    role="presentation"
  >
    <div class="sheet" role="dialog" aria-modal="true">
      <div class="sheet-handle" aria-hidden="true"></div>
      {#if title}
        <header class="sheet-header">
          <span class="sheet-title">{title}</span>
          <button class="sheet-close" onclick={onclose} aria-label="閉じる">×</button>
        </header>
      {/if}
      <div class="sheet-body">
        {@render children()}
      </div>
    </div>
  </div>
{/if}

<style>
  .sheet-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(2px);
    -webkit-backdrop-filter: blur(2px);
    z-index: 100;
    display: flex;
    align-items: flex-end;
    justify-content: center;
    animation: fade-in 0.18s ease-out;
    transition: opacity 0.2s ease-out, backdrop-filter 0.2s ease-out;
  }

  /* プレビュー演出再生中: シートを半透明化して背後の演出を確認可能にする */
  .sheet-backdrop.fade-out {
    opacity: 0.12;
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
    pointer-events: none;
  }

  @keyframes fade-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  .sheet {
    width: 100%;
    max-width: 600px;
    max-height: 88vh;
    background: var(--color-surface);
    border-top-left-radius: var(--radius-lg);
    border-top-right-radius: var(--radius-lg);
    display: flex;
    flex-direction: column;
    padding-bottom: var(--safe-bottom);
    box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.2);
    animation: slide-up 0.24s cubic-bezier(0.32, 0.72, 0, 1);
  }

  @keyframes slide-up {
    from { transform: translateY(100%); }
    to { transform: translateY(0); }
  }

  .sheet-handle {
    width: 40px;
    height: 4px;
    background: var(--color-border);
    border-radius: 2px;
    margin: 8px auto 4px;
    flex-shrink: 0;
  }

  .sheet-header {
    display: flex;
    align-items: center;
    padding: var(--space-sm) var(--space-md);
    border-bottom: 1px solid var(--color-border);
    flex-shrink: 0;
  }

  .sheet-title {
    flex: 1;
    font-size: var(--font-size-base);
    font-weight: 600;
  }

  .sheet-close {
    font-size: 24px;
    line-height: 1;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--color-fg-muted);
    border-radius: var(--radius-sm);
  }

  .sheet-close:active {
    background: var(--color-border);
  }

  .sheet-body {
    flex: 1;
    overflow-y: auto;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
    padding: var(--space-md);
  }
</style>
